import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, Share2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/i18n";

const THEMES = {
  linen: { name: "Linen", bg: "#FAF8F5", head: "#1C242B", headText: "#FAF8F5", headSub: "#CBD5E1", over: "#F0A584", label: "#B45309", text: "#1C242B", sub: "#64748B", a1: "#D96B43", a2: "#3E6B5C", track: "#E7E1D8" },
  midnight: { name: "Midnight", bg: "#0F1416", head: "#182024", headText: "#F0F4F8", headSub: "#94A3B8", over: "#F0A584", label: "#F0A584", text: "#F0F4F8", sub: "#94A3B8", a1: "#F0A584", a2: "#9FD3BF", track: "#26323A" },
  sage: { name: "Sage", bg: "#EEF4F0", head: "#3E6B5C", headText: "#FFFFFF", headSub: "#D5E3DA", over: "#FCE3D6", label: "#2F5548", text: "#1C242B", sub: "#4B5D55", a1: "#D96B43", a2: "#1C242B", track: "#D5E3DA" },
  terracotta: { name: "Terracotta", bg: "#FFF7F2", head: "#D96B43", headText: "#FFFFFF", headSub: "#FDE1D3", over: "#1C242B", label: "#C05630", text: "#1C242B", sub: "#6B5B53", a1: "#1C242B", a2: "#3E6B5C", track: "#F3DED3" },
};
const FORMATS = {
  portrait: { name: "Portrait 4:5", sub: "Instagram, Facebook", w: 1080, h: 1350, head: 420, items: 3, gap: 74, careers: 4 },
  square: { name: "Square 1:1", sub: "LinkedIn, WhatsApp", w: 1080, h: 1080, head: 330, items: 2, gap: 70, careers: 3 },
};

const drawLogo = (x, X, Y, S) => {
  const k = S / 64;
  const rr = (rx, ry, w, h, r, c) => { x.fillStyle = c; x.beginPath(); x.roundRect(X + rx * k, Y + ry * k, w * k, h * k, r * k); x.fill(); };
  rr(0, 0, 64, 64, 16, "#1C242B"); rr(27, 27, 24, 10, 5, "#D96B43"); rr(13, 27, 24, 10, 5, "#FAF8F5"); rr(27, 13, 10, 38, 5, "#FAF8F5");
  x.fillStyle = "#3E6B5C"; x.beginPath(); x.arc(X + 50 * k, Y + 14 * k, 4 * k, 0, Math.PI * 2); x.fill();
};

const draw = async (data, t, f, tr) => {
  await document.fonts.ready;
  const c = document.createElement("canvas");
  c.width = f.w; c.height = f.h;
  const x = c.getContext("2d"), W = f.w, P = 80;
  x.direction = "ltr";
  x.fillStyle = t.bg; x.fillRect(0, 0, W, f.h);
  x.fillStyle = t.head; x.fillRect(0, 0, W, f.head);
  x.fillStyle = t.a1 === t.head ? t.a2 : t.a1; x.fillRect(0, f.head, W, 10);
  x.fillStyle = t.over; x.font = "600 26px 'JetBrains Mono', monospace"; x.fillText(tr("MY STRENGTHS PROFILE"), P, f.head * 0.26);
  x.fillStyle = "rgba(255,255,255,0.16)"; x.beginPath(); x.roundRect(W - P - 90, f.head * 0.26 - 58, 96, 96, 26); x.fill();
  drawLogo(x, W - P - 84, f.head * 0.26 - 52, 84);
  x.fillStyle = t.headText; x.font = `800 ${Math.round(f.head * 0.47)}px Outfit, sans-serif`; x.fillText(data.holland, P - 8, f.head * 0.74);
  x.fillStyle = t.headSub; x.font = "500 30px Figtree, sans-serif"; x.fillText(data.hollandWords, P, f.head * 0.9);
  let top = f.head;
  if (data.quote) {
    let size = 36;
    const q = `“${data.quote}”`;
    do { x.font = `italic 500 ${size}px 'Playfair Display', serif`; size -= 2; } while (x.measureText(q).width > W - 2 * P && size > 20);
    x.fillStyle = t.text; x.fillText(q, P, f.head + 78);
    top += 70;
  }
  const block = (title, items, y, color) => {
    x.fillStyle = t.label; x.font = "600 24px 'JetBrains Mono', monospace"; x.fillText(title, P, y);
    items.slice(0, f.items).forEach((it, i) => {
      const yy = y + 50 + i * f.gap;
      x.fillStyle = t.text; x.font = "600 32px Figtree, sans-serif"; x.fillText(it.label, P, yy);
      x.font = "600 28px 'JetBrains Mono', monospace"; x.fillStyle = color; x.textAlign = "right"; x.fillText(String(Math.round(it.score)), W - P, yy); x.textAlign = "left";
      x.fillStyle = t.track; x.fillRect(P, yy + 16, W - 2 * P, 10);
      x.fillStyle = color; x.fillRect(P, yy + 16, (W - 2 * P) * Math.min(1, it.score / 100), 10);
    });
    return y + 50 + f.items * f.gap + 20;
  };
  let y = block(tr("TOP APTITUDES"), data.aptitudes, top + 90, t.a1);
  y = block(tr("TOP INTELLIGENCES"), data.intelligences, y + 10, t.a2);
  x.fillStyle = t.label; x.font = "600 24px 'JetBrains Mono', monospace"; x.fillText(tr("CAREERS THAT FIT ME"), P, y + 10);
  x.fillStyle = t.text; x.font = "500 30px Figtree, sans-serif";
  let line = "", ly = y + 58;
  const nC = data.quote ? f.careers - 1 : f.careers;
  data.careers.slice(0, nC).forEach((w, i) => {
    const tt = line ? `${line}  ·  ${w}` : w;
    if (x.measureText(tt).width > W - 2 * P && line) { x.fillText(line, P, ly); ly += 44; line = w; } else line = tt;
    if (i === Math.min(nC, data.careers.length) - 1) x.fillText(line, P, ly);
  });
  drawLogo(x, P, f.h - 92, 48);
  x.fillStyle = t.text; x.font = "700 28px Outfit, sans-serif"; x.fillText("Aptitude Plus", P + 64, f.h - 60);
  x.fillStyle = t.sub; x.font = "500 24px Figtree, sans-serif"; x.textAlign = "right"; x.fillText(tr("Free test at aptitudeplus.org"), W - P, f.h - 60); x.textAlign = "left";
  return c;
};

const Pick = ({ active, onClick, children, testId }) => (
  <button data-testid={testId} onClick={onClick}
    className={`rounded-full px-3.5 py-1.5 text-sm border transition-colors ${active ? "bg-[#1C242B] text-white border-[#1C242B]" : "bg-white border-[#1C242B]/15 hover:border-[#1C242B]/40"}`}>
    {children}
  </button>
);

export const StrengthsCardDialog = ({ result }) => {
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState("linen");
  const [format, setFormat] = useState("portrait");
  const [preview, setPreview] = useState(null);
  const [quote, setQuote] = useState("");
  const { t: tr } = useLang();
  const st = result.strengths;
  const data = {
    holland: result.profile.holland_code,
    hollandWords: result.profile.holland_code.split("").map((k) => result.riasec_names[k]).join(" · "),
    aptitudes: st.top_aptitudes.map((a) => ({ label: a.label, score: a.score })),
    intelligences: st.top_intelligences.map((a) => ({ label: tr(a.label), score: a.score })),
    careers: result.careers.slice(0, 4).map((c) => c.title),
    quote: quote.trim(),
  };
  const shareText = `My career strengths: Holland code ${data.holland} (${data.hollandWords}). Top intelligence: ${data.intelligences[0]?.label}. Discover yours free at ${window.location.origin}`;
  const fileName = `my-strengths-${data.holland}-${format}-${theme}.png`;

  useEffect(() => {
    if (!open) return;
    draw(data, THEMES[theme], FORMATS[format], tr).then((c) => setPreview(c.toDataURL("image/png")));
  }, [open, theme, format, quote]); // eslint-disable-line react-hooks/exhaustive-deps

  const download = () => {
    const a = document.createElement("a");
    a.download = fileName; a.href = preview; a.click();
    toast.success("Strengths card downloaded.");
  };

  const nativeShare = async () => {
    const blob = await (await fetch(preview)).blob();
    const file = new File([blob], fileName, { type: "image/png" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: "My strengths card", text: shareText }); } catch { /* user cancelled */ }
    } else {
      download();
      toast.message("Your device can't share images directly. The card was downloaded, so attach it in your app.");
    }
  };

  const site = encodeURIComponent(window.location.origin);
  const webLinks = [
    ["whatsapp", "WhatsApp", `https://wa.me/?text=${encodeURIComponent(shareText)}`],
    ["linkedin", "LinkedIn", `https://www.linkedin.com/sharing/share-offsite/?url=${site}`],
    ["facebook", "Facebook", `https://www.facebook.com/sharer/sharer.php?u=${site}&quote=${encodeURIComponent(shareText)}`],
  ];

  return (
    <>
      <button data-testid="download-strengths-card-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-[#1C242B]/15 bg-white px-4 py-2 text-sm hover:border-[#1C242B]/40 transition-colors">
        <ImageIcon size={14} /> Strengths card
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl bg-[#FAF8F5] max-h-[92vh] overflow-y-auto" data-testid="strengths-card-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Your strengths card</DialogTitle>
            <DialogDescription>Pick a style and size, then download or share it. Your private results link is never included.</DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-[1fr_1fr] gap-6 mt-2">
            <div className="rounded-xl overflow-hidden border border-[#1C242B]/10 bg-white grid place-items-center">
              {preview ? <img src={preview} alt="Strengths card preview" className="w-full h-auto" data-testid="strengths-card-preview" /> : <div className="h-64" />}
            </div>
            <div className="flex flex-col gap-5">
              <div>
                <p className="text-xs font-mono-ui text-slate-500 mb-2">YOUR PERSONAL LINE (OPTIONAL)</p>
                <input data-testid="card-quote-input" value={quote} maxLength={70} onChange={(e) => setQuote(e.target.value)}
                  placeholder="Looking for my next chapter in healthcare"
                  className="w-full rounded-full border border-[#1C242B]/15 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D96B43]/40" />
                <p className="text-[11px] text-slate-400 mt-1 text-right">{quote.length}/70</p>
              </div>
              <div>
                <p className="text-xs font-mono-ui text-slate-500 mb-2">THEME</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(THEMES).map(([k, t]) => (
                    <Pick key={k} active={theme === k} onClick={() => setTheme(k)} testId={`card-theme-${k}`}>
                      <span className="inline-block w-3 h-3 rounded-full mr-1.5 align-middle border border-black/10" style={{ background: t.head }} />{t.name}
                    </Pick>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-mono-ui text-slate-500 mb-2">SIZE</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(FORMATS).map(([k, f]) => (
                    <Pick key={k} active={format === k} onClick={() => setFormat(k)} testId={`card-format-${k}`}>{f.name} <span className="opacity-60 text-xs">· {f.sub}</span></Pick>
                  ))}
                </div>
              </div>
              <div className="grid gap-2.5">
                <button data-testid="card-share-native-btn" onClick={nativeShare} disabled={!preview}
                  className="inline-flex justify-center items-center gap-2 rounded-full bg-[#D96B43] text-white px-5 py-3 text-sm font-semibold hover:bg-[#C05630] disabled:opacity-50 transition-colors">
                  <Share2 size={16} /> Share image (WhatsApp, LinkedIn, Facebook…)
                </button>
                <button data-testid="card-download-btn" onClick={download} disabled={!preview}
                  className="inline-flex justify-center items-center gap-2 rounded-full bg-[#1C242B] text-white px-5 py-3 text-sm font-semibold hover:bg-[#3E6B5C] disabled:opacity-50 transition-colors">
                  <Download size={16} /> Download PNG
                </button>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-2">On a computer? Download the card, then open a post and attach it:</p>
                <div className="flex flex-wrap gap-2">
                  {webLinks.map(([k, l, u]) => (
                    <a key={k} href={u} target="_blank" rel="noopener noreferrer" data-testid={`card-share-${k}-link`}
                      className="rounded-full border border-[#1C242B]/15 bg-white px-4 py-1.5 text-sm hover:border-[#D96B43] hover:text-[#C05630] transition-colors">{l}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
